import { useEffect, useState } from "react";
import { Download, Loader2, X } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PdfCanvasViewer } from "@/components/pdf-canvas-viewer";
import { apiUrl } from "@/apiOrigin";
import { getToken } from "@/lib/api";

/**
 * Reusable preview sheet for branded customer-facing PDFs.
 *
 * Used by office-review (and anywhere else that lists invoices /
 * estimates) so the office can sanity-check the customer-facing output
 * before hitting Send. Fetches the PDF from
 * `/api/reports/{kind}/{id}/pdf` with the auth token and renders via
 * pdf.js canvas so iPad standalone PWAs stay in-app.
 */
export interface DocumentPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    /** Drives both the URL (`/api/reports/{kind}/...`) and the dialog
     *  title. */
    kind: "invoice" | "estimate";
    /** UUID of the document to render. Required when `open` is true. */
    documentId: string | null;
    /** Human label shown in the header (e.g. invoice number). Falls
     *  back to "Invoice" / "Estimate" when missing. */
    label?: string | null;
    /** Filename used by the Download button. Optional — the server
     *  Content-Disposition wins when present. */
    suggestedFilename?: string | null;
}

export function DocumentPreviewDialog({
    open,
    onClose,
    kind,
    documentId,
    label,
    suggestedFilename,
}: DocumentPreviewDialogProps) {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloadFilename, setDownloadFilename] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !documentId) return;
        let cancelled = false;
        let createdUrl: string | null = null;
        setLoading(true);
        setError(null);
        setBlobUrl(null);
        setPdfData(null);
        const token = getToken();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        fetch(apiUrl(`/api/reports/${kind}/${documentId}/pdf`), { headers })
            .then(async (res) => {
                if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
                const blob = await res.blob();
                const disposition = res.headers.get("Content-Disposition");
                let filename = suggestedFilename ?? `${kind}-${documentId.slice(0, 8)}.pdf`;
                if (disposition) {
                    const match = disposition.match(/filename="?([^"]+)"?/);
                    if (match) filename = match[1];
                }
                if (cancelled) return;
                createdUrl = URL.createObjectURL(blob);
                setBlobUrl(createdUrl);
                setDownloadFilename(filename);
                setPdfData(await blob.arrayBuffer());
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : "Preview failed");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [open, kind, documentId, suggestedFilename]);

    useEffect(() => {
        if (!open) {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
            setBlobUrl(null);
            setPdfData(null);
        }
    }, [open, blobUrl]);

    const titleLabel = label?.trim() ? label : kind === "invoice" ? "Invoice" : "Estimate";
    const title = `Preview · ${titleLabel}`;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={title}
            className="!max-w-5xl w-[min(96vw,1100px)] !p-0 max-h-[92vh]"
        >
            <div className="flex h-[78vh] flex-col">
                <div className="relative flex flex-1 flex-col overflow-hidden bg-muted/40">
                    {loading && (
                        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rendering preview…
                        </div>
                    )}
                    {error && !loading && (
                        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-destructive">
                            <X className="h-5 w-5" />
                            <span>{error}</span>
                        </div>
                    )}
                    {pdfData && !error && (
                        <PdfCanvasViewer data={pdfData} className="relative flex h-full w-full flex-col" />
                    )}
                </div>
                <div className="flex items-center justify-end gap-2 border-t bg-card px-5 py-3">
                    <Button variant="outline" onClick={onClose}>
                        Close
                    </Button>
                    <Button
                        variant="outline"
                        disabled={!blobUrl || !downloadFilename}
                        onClick={() => {
                            if (!blobUrl || !downloadFilename) return;
                            const a = document.createElement("a");
                            a.href = blobUrl;
                            a.download = downloadFilename;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                        }}
                    >
                        <Download className="mr-1.5 h-4 w-4" /> Download
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
